/**
 * @fileoverview A component that provides a dropdown for selecting the study of a participant.
 * It uses the `@headlessui/react` library for the dropdown and transition.
 *
 * @example
 * <StudyDropdown currentStudyId={currentStudyId} onChange={handleStudyChange} />
 */

import { Listbox, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import StudyIcon from '../assets/menu/studies.svg'
import { useGetStudiesQuery } from '../services/studies'

/**
 * @interface StudyDropdownProps
 * @description Props for the StudyDropdown component.
 * @property {string} currentStudyId - The current study ID.
 * @property {(studyId: string) => void} onChange - The function to call when the study is changed.
 */
interface StudyDropdownProps {
    currentStudyId?: string
    onChange: (studyId: string) => void
}

/**
 * @function StudyDropdown
 * @description Component for providing a dropdown for selecting the study of a participant.
 * @param {StudyDropdownProps} props - The props for the StudyDropdown component.
 * @returns {JSX.Element | null} - The StudyDropdown component.
 */
export default function StudyDropdown({ onChange, currentStudyId }: StudyDropdownProps) {
    const { data: studies, isLoading } = useGetStudiesQuery()

    if (isLoading) {
        return null
    }

    const current = currentStudyId ? studies.find((item) => item.id === currentStudyId) : studies[0]
    const isDisabled = !(studies.length > 1)

    return (
        <div className="relative flex rounded-md">
            <Listbox
                disabled={isDisabled}
                as="div"
                className="relative -ml-px block rounded-md"
                value={current?.id}
                onChange={onChange}
            >
                {current && (
                    <Listbox.Button
                        as="button"
                        className="flex group gap-x-1 -ml-1 pl-0.5 pr-2 py-0.5 items-center rounded text-gray-900 enabled:hover:text-indigo-700 enabled:cursor-pointer enabled:hover:bg-indigo-100"
                    >
                        <div className="flex gap-x-2 items-center">
                            <StudyIcon className="fill-indigo-500 stroke-white enabled:group-hover:stroke-indigo-100" />
                            {current.name}
                        </div>
                        {!isDisabled && (
                            <ChevronDownIcon className="w-5 h-5 ml-2 -mr-1 text-gray-400 group-hover:text-indigo-600" />
                        )}
                    </Listbox.Button>
                )}

                <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Listbox.Options className="group absolute overflow-y-auto max-h-[300px] -left-3 z-10 mt-2 w-[312px] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {studies
                            .filter(({ id }) => id !== current?.id)
                            .map(({ name, id }) => (
                                <Listbox.Option
                                    key={name}
                                    className="w-full hover:bg-gray-100"
                                    value={id}
                                >
                                    <div className="cursor-pointer flex gap-x-2 px-3 py-2 items-center">
                                        <StudyIcon className="fill-indigo-500 stroke-white group-hover:stroke-gray-100" />
                                        {name}
                                    </div>
                                </Listbox.Option>
                            ))}
                    </Listbox.Options>
                </Transition>
            </Listbox>
        </div>
    )
}
